import React, { useState } from 'react'
import { Alert, Button, Form, FormControl, Modal, Spinner } from 'react-bootstrap'
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useCreateCronJobMutation } from '../../../store/api/cronJobsApi';
import { CreateCronJobRequest } from '../../../store/api/types';
import { cronJobValidationSchema } from '../../../utils/validators';

type CreateCronJobModalProps = {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

const defaultErrorMsg = 'Oops. Something went wrong. Please, try again later. ';

const CreateCronJobModal: React.FC<CreateCronJobModalProps> = ({ show, onHide, onSuccess }) => {

    const [createCronJob, { isLoading }] = useCreateCronJobMutation();
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<CreateCronJobRequest>({
        defaultValues: {
            active: false,
            description: ''
        },
        resolver: yupResolver(cronJobValidationSchema)
    });

    const onModalHide = () => {
        setErrorMsg(null);
        onHide();
    }

    const onSubmit = async (data: CreateCronJobRequest) => {
        const response = await createCronJob({
            ...data,
            description: data.description || null,
        });

        if ('error' in response) {
            const err = response.error as any;
            setErrorMsg(err.data?.message || defaultErrorMsg);
        }
        else {
            reset();
            setErrorMsg(null);
            onSuccess();
        }
    }

    return (
        <Modal
            size="lg"
            centered
            show={show}
            onHide={onModalHide}
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Create Cron Job
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMsg && <Alert variant='danger' onClose={() => setErrorMsg(null)} dismissible>
                    {errorMsg}
                </Alert>}
                <Form className='d-flex flex-column' onSubmit={handleSubmit(onSubmit)}>

                    <FormControl
                        {...register('title')}
                        className={`mt-3 ${errors.title ? 'is-invalid' : ''}`}
                        maxLength={255}
                        placeholder='Title' />
                    <div className="invalid-feedback align-self-start">{errors.title?.message}</div>

                    <FormControl
                        {...register('alias')}
                        className={`mt-3 ${errors.alias ? 'is-invalid' : ''}`}
                        maxLength={255}
                        placeholder='Alias' />
                    <div className="invalid-feedback align-self-start">{errors.alias?.message}</div>

                    <FormControl
                        {...register('description')}
                        className={`mt-3 ${errors.description ? 'is-invalid' : ''}`}
                        maxLength={255}
                        placeholder='Description'
                        as="textarea"
                        rows={3} />
                    <div className="invalid-feedback align-self-start">{errors.description?.message}</div>

                    <Form.Group className="mb-3 mt-3">
                        <Form.Check
                            {...register('active')}
                            type='checkbox'
                            label='Active'
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='outline-danger' onClick={onModalHide}>Close</Button>
                <Button variant='outline-success' onClick={handleSubmit(onSubmit)} disabled={isLoading}>
                    {isLoading && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true" />}
                    <span className="sr-only">Create</span>
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default CreateCronJobModal
